import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import useRestaurantData from '../hooks/useRestaurantData';
import { useApp } from '../context/AppContext';
import Header from '../components/Header';
import LandingPage from '../components/LandingPage';
import MenuPage from '../components/MenuPage';
import ItemModal from '../components/ItemModal';
import Cart from '../components/Cart';
import CartIcon from '../components/CartIcon';
import LoadingScreen from '../components/LoadingScreen';
import ErrorScreen from '../components/ErrorScreen';

/**
 * RestaurantPage — loaded at /:slug
 * Fetches the restaurant JSON and renders the full experience:
 *   Header → Landing → Menu → Item Modal → Cart
 */
export default function RestaurantPage() {
  const { slug } = useParams();
  const { data, loading, error, retry } = useRestaurantData(slug);
  const { t } = useApp();

  // UI state
  const [view, setView] = useState('landing'); // 'landing' | 'menu'
  const [selectedItem, setSelectedItem] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen message={error} onRetry={retry} />;
  if (!data) return null;

  const { restaurant, categories } = data;

  return (
    <>
      {/* Top header bar with language/theme toggles */}
      <Header
        restaurant={restaurant}
        view={view}
        onBack={() => setView('landing')}
      />

      {/* Landing page (hero) */}
      {view === 'landing' && (
        <LandingPage
          restaurant={restaurant}
          onViewMenu={() => setView('menu')}
        />
      )}

      {/* Menu page */}
      {view === 'menu' && (
        <MenuPage
          categories={categories}
          restaurant={restaurant}
          onSelectItem={setSelectedItem}
        />
      )}

      {/* Item detail modal */}
      {selectedItem && (
        <ItemModal
          item={selectedItem}
          restaurant={restaurant}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {/* Floating cart button */}
      {view === 'menu' && !cartOpen && (
        <CartIcon onClick={() => setCartOpen(true)} />
      )}

      {/* Cart drawer */}
      {cartOpen && (
        <Cart
          restaurant={restaurant}
          onClose={() => setCartOpen(false)}
        />
      )}
    </>
  );
}
